<template>
  <div>
    <v-card outlined>
      <v-card-title class="grey lighten-3">
        <slot name="title">{{ title }}</slot>
        <v-spacer></v-spacer>
        <v-switch
          class="mt-0 mr-2"
          v-model="showServiceLimitTime"
          label="Show active items"
          hide-details
        ></v-switch>
        <slot name="headerButtons"></slot>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="filteredHeadersTable"
          :items="rules"
          dense
          :footer-props="{ 'items-per-page-options': [15, 50, 100, -1] }"
          :items-per-page="50"
          :options.sync="options"
          :server-items-length="totalRules"
          multi-sort
          :loading="loading"
          :item-class="itemRowBackground"
        >
          <!-- Header slots -->
          <template v-slot:header="{ props: { headers } }">
            <tr v-show="$vuetify.breakpoint.smAndUp">
              <th v-for="header in headers" :key="header.value">
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 mt-6 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  ></v-text-field>
                </div>

                <div
                  v-else-if="header.type === 'date'"
                  class="mx-2 font-weight-light"
                >
                  <date-picker
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  ></date-picker>
                </div>

                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 mt-6 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    item-text="text"
                    item-value="value"
                    @change="searchUpdated"
                    outlined
                    clearable
                  ></v-select>
                </div>

                <div
                  v-else-if="header.type === 'multiselect'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    @change="searchUpdated"
                    outlined
                    clearable
                    multiple
                  ></v-select>
                </div>
              </th>
            </tr>

            <!-- XS only -->
            <tr
              v-show="$vuetify.breakpoint.xsOnly"
              v-for="header in headers"
              :key="header.value"
            >
              <th>
                <div
                  v-if="header.type === 'text'"
                  class="mx-2 font-weight-light"
                >
                  <v-text-field
                    dense
                    :label="header.text"
                    clearable
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  ></v-text-field>
                </div>

                <div
                  v-else-if="header.type === 'select'"
                  class="mx-2 font-weight-light"
                >
                  <v-select
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    :items="headerSelects[header.value]"
                    @change="searchUpdated"
                    outlined
                    clearable
                  ></v-select>
                </div>

                <div
                  v-else-if="header.type === 'date'"
                  class="mx-2 font-weight-light"
                >
                  <date-picker
                    dense
                    :label="header.text"
                    v-model="search[header.value]"
                    @input="searchUpdated"
                    outlined
                  ></date-picker>
                </div>
              </th>
            </tr>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-center">
              <v-btn
                icon
                color="primary"
                class="mr-2"
                @click.stop="routeToRule(item)"
                v-if="item.canEdit"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <v-btn
                icon
                color="red"
                @click.stop="$emit('deleteRule', item)"
                v-if="item.canDelete"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template></v-data-table
        ></v-card-text
      ></v-card
    >
  </div>
</template>

<script>
// UI uses `documentType`, backend uses `serviceRequestType`

import _ from "lodash";
import DatePicker from "@/components/common/DatePicker.vue";

export default {
  name: "DataTable",
  props: {
    title: {
      typeof: String,
      default: "Search Results",
    },
    optionsPassed: {
      typeof: Object,
      required: true,
    },
    totalRules: {
      typeof: Number,
      required: true,
    },
    rules: {
      typeof: Array,
      required: true,
    },
    searchPassed: {
      typeof: Array,
      required: true,
    },
    headerSelects: {
      typeof: Array,
      required: true,
    },
    headersTable: {
      typeof: Array,
      required: true,
    },
    loading: Boolean,
    allowIgnoreServiceLimitTIme: {
      typeof: Boolean,
      default: true,
    },
    showHasApproved: {
      typeof: Boolean,
      default: false,
    },
    routeToDetails: {
      typeof: Boolean,
      default: true,
    },
  },
  components: {
    ApprovalSchematic: () => import("@/components/ESV/ApprovalSchematic.vue"),
    DatePicker,
  },
  data() {
    return {
      // loading: false,
      searchTimeOut: null,
      options: this.optionsPassed,
      search: this.searchPassed,
      showServiceLimitTime: true,
      pendingSearchUpdate: false,
    };
  },
  computed: {
    filteredHeadersTable() {
      if (this.search.serviceRequestType === 24) {
        return this.headersTable.filter(
          (header) => header.value !== "serviceGrade"
        );
      }
      return this.headersTable;
    },
  },
  methods: {
    routeToRule(rule) {
      if (this.routeToDetails) {
        this.$router.push({ name: "AddRule", params: { ruleId: rule.id } });
      } else {
        this.$emit("row:click", rule);
      }
    },
    getOrganisations() {
      if (
        !this.headerSelects["organisation"] ||
        this.headerSelects["organisation"].length === 0
      ) {
        this.$api
          .getOrganisations()
          .then((resp) => {
            this.$set(this.headerSelects, "organisation", resp.data);
          })
          .catch((err) => {
            console.error("Error fetching organisations:", err);
          });
      }
    },
    async getDocumentTypes() {
      if (
        !this.headerSelects["documentType"] ||
        this.headerSelects["documentType"].length === 0
      ) {
        try {
          const resp = await this.$api.getServiceRequestTypes();

          const filtered = resp.data.filter(item =>
            item.text === "ESV" || item.text === "RNS"
          );

          this.$set(this.headerSelects, "documentType", filtered);
          console.log("Filtered documentTypes:", filtered);
        } catch (err) {
          console.error("❌ Error fetching documentType:", err);
        }
      }
    },

    async getCountries() {
      if (
        !this.headerSelects["country"] ||
        this.headerSelects["country"].length === 0
      ) {
        try {
          const resp = await this.$api.getCountries();
          this.$set(this.headerSelects, "country", resp.data);
        } catch (err) {
          console.error("Error fetching countries:", err);
        }
      }
    },
    getSBMProfiles() {
      if (
        !this.headerSelects["sbmProfile"] ||
        this.headerSelects["sbmProfile"].length === 0
      ) {
        this.$api
          .getSBMProfiles()
          .then((resp) => {
            this.$set(this.headerSelects, "sbmProfile", resp.data);
          })
          .catch((err) => {
            console.error("Error fetching sbmProfiles:", err);
          });
      }
    },
    async getServiceGrades() {
      try {
        const resp = await this.$api.getServiceGrades();
        //exclude Special Project
        const filtered = resp.data.filter((item) => item.value !== 32);

        this.$set(this.headerSelects, "serviceGrade", filtered);
      } catch (err) {
        console.error("❌ Error fetching serviceGrades:", err);
      }
    },
    getProductGroups() {
      this.$api
        .getProductGroups()
        .then((resp) => (this.headerSelects["productGroup"] = resp.data));
    },
    searchUpdated: _.throttle(function () {
      let sendSearch = {
        options: this.options,
        search: this.search,
        ignoreServiceLimitTime: !this.showServiceLimitTime,
      };
      this.$emit("getCommunicationRules", sendSearch);
    }, 500),
    itemRowBackground(item) {
      if (!this.showServiceLimitTime) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const from = item.activeDateFrom ? new Date(item.activeDateFrom) : null;
        const to = item.activeDateTo ? new Date(item.activeDateTo) : null;

        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(0, 0, 0, 0);

        const isActive = (!from || from <= today) && (!to || today <= to);
        return isActive
          ? "activeEsv cursor-pointer"
          : "inactiveEsv cursor-pointer";
      }

      if (this.showHasApproved) {
        return item.hasApproved
          ? "activeEsv cursor-pointer"
          : "inactiveEsv cursor-pointer";
      }

      return "cursor-pointer";
    },
  },
  created() {},
  mounted() {
    //this.getStatusses();
    this.getDocumentTypes();
    this.getServiceGrades();
    this.getProductGroups();
    this.getOrganisations();
    this.getCountries();
    this.getSBMProfiles();
  },
  watch: {
    "search.productGroup": {
      handler(newVal, oldVal) {
        this.searchUpdated();
      },
      deep: true,
      immediate: false,
    },
    options: {
      handler() {
        this.searchUpdated();
      },
      deep: true,
    },
    showServiceLimitTime: {
      handler() {
        if (this.loading) {
          this.pendingSearchUpdate = true;
        } else {
          this.searchUpdated();
        }
      },
    },
    loading: {
      handler(newValue) {
        if (!newValue && this.pendingSearchUpdate) {
          this.pendingSearchUpdate = false;
          this.searchUpdated();
        }
      },
    },
  },
};
</script>

<style>
.activeEsv {
  background-color: rgb(143, 255, 152);
}
.inactiveEsv {
  background-color: rgb(255, 124, 124);
}
</style>